import { jsx, jsxs } from "react/jsx-runtime";
import { useThemeProps } from "@mui/material";
import { useSlotProps } from "../../base/index.js";
import { simpleSelectName } from "./classes.js";
import { useSimpleSelect } from "./hooks/useSimpleSelect.js";
import { useUtilityClasses } from "./hooks/useUtilityClasses.js";
import { HelperText } from "./components/HelperText.js";
import { Label } from "./components/Label.js";
import { Root } from "./components/Root.js";
import { Select } from "./components/Select.js";
import { createIconsComponent } from "./utils/createIconsComponent.js";
import { defaultRenderOptions } from "./utils/defaultRenderOptions.js";
import { defaultRenderValue } from "./utils/defaultRenderValue.js";
function SimpleSelect(inProps) {
  const props = useThemeProps({
    name: simpleSelectName,
    props: inProps
  });
  const {
    sx,
    ref,
    testId = `${simpleSelectName}-root`,
    className = "",
    components,
    componentsProps,
    name,
    size,
    label,
    value,
    error,
    onClear,
    options,
    required,
    disabled,
    fullWidth,
    helperText,
    renderValue = defaultRenderValue,
    renderOptions = defaultRenderOptions,
    ...rest
  } = props;
  const {
    open,
    focused,
    mouseIn,
    MenuProps,
    handleOpen,
    handleClose,
    handleOnBlur,
    handleOnFocus,
    handleToggleOpen,
    handleOnMouseEnter,
    handleOnMouseLeave
  } = useSimpleSelect(props);
  const labelId = name ? `${name}-label` : void 0;
  const fieldHasError = !!error;
  const ownerState = props;
  const utilityClasses = useUtilityClasses(ownerState);
  const rootSlotProps = useSlotProps({
    className: `${className} ${utilityClasses.root}`,
    ownerState,
    elementType: Root,
    additionalProps: {
      sx,
      as: components?.Root,
      ref,
      "data-tidal": simpleSelectName,
      "data-testid": testId
    },
    externalSlotProps: componentsProps?.root
  });
  const labelSlotProps = useSlotProps({
    className: utilityClasses.label,
    ownerState,
    elementType: Label,
    additionalProps: { as: components?.Label },
    externalSlotProps: componentsProps?.label
  });
  const selectSlotProps = useSlotProps({
    className: utilityClasses.select,
    ownerState,
    elementType: Select,
    externalSlotProps: componentsProps?.select
  });
  const helperTextSlotProps = useSlotProps({
    className: utilityClasses.helperText,
    ownerState,
    elementType: HelperText,
    additionalProps: { as: components?.HelperText },
    externalSlotProps: componentsProps?.helperText
  });
  return /* @__PURE__ */ jsxs(Root, { ...rootSlotProps, size, error, disabled, required, fullWidth, children: [
    /* @__PURE__ */ jsx(Label, { ...labelSlotProps, id: labelId, children: label }),
    /* @__PURE__ */ jsx(
      Select,
      {
        ...selectSlotProps,
        ...rest,
        ref,
        open,
        value,
        label,
        onOpen: handleOpen,
        onBlur: handleOnBlur,
        labelId,
        onClose: handleClose,
        onFocus: handleOnFocus,
        disabled,
        MenuProps,
        onMouseEnter: handleOnMouseEnter,
        onMouseLeave: handleOnMouseLeave,
        IconComponent: createIconsComponent({
          focused,
          mouseIn,
          isEmpty: !value,
          disabled,
          fieldHasError,
          handleToggleOpen,
          handleOnClearInput: onClear
        }),
        renderValue: (selected) => {
          const selectedOption = options.find((opt) => opt.value === selected);
          return renderValue(selectedOption, props);
        },
        children: renderOptions(options)
      }
    ),
    !!helperText && /* @__PURE__ */ jsx(HelperText, { ...helperTextSlotProps, required, error, disabled, children: helperText })
  ] });
}
export {
  SimpleSelect
};
//# sourceMappingURL=SimpleSelect.js.map
